import { useMemo, useState } from "react";
import { Chart } from "@highcharts/react";
import type Highcharts from "highcharts/es-modules/masters/highcharts.src.js";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { Box, Flex } from "../../../../styled-system/jsx";
import { ButtonGroup } from "../../../components/core/basic/ButtonGroup";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import type { Audience, ReportSummary, WorkSplit } from "../model";
import { chartFont, INK, PREVIOUS_PERIOD_COLOR, TEAM_COLOR, TRELLIS_COLOR } from "../theme";
import { useActivityLabels } from "../useActivityLabels";
import { Section, useCount } from "./primitives";

type TrendMetric = "activePeople" | "changes" | "views";

const dateOf = (isoDay: string) => new Date(`${isoDay}T12:00:00Z`);

const axisStyle = { color: INK.muted, fontSize: "12px" };

export function ActivityTrendChart({ summary, audience }: { summary: ReportSummary; audience: Audience }) {
  const { t } = useTranslation(["user_activity"]);
  const axisDate = useDateFormatter("monthDay");
  const longDate = useDateFormatter("medium");
  const count = useCount();
  const [metric, setMetric] = useState<TrendMetric>("activePeople");
  const color = audience === "trellis" ? TRELLIS_COLOR : TEAM_COLOR;

  const metricLabel = useMemo<Record<TrendMetric, string>>(
    () => ({
      activePeople: t(($) => $.user_activity["People"]),
      changes: t(($) => $.user_activity["Changes"]),
      views: t(($) => $.user_activity["Page views"]),
    }),
    [t],
  );

  const options = useMemo<Highcharts.Options>(() => {
    const current = summary.daily.map((point) => point[metric]);
    const previous = summary.previousDaily.map((point) => point[metric]);
    const step = Math.max(1, Math.ceil(summary.days.length / 8));
    return {
      chart: { height: 280, style: chartFont, spacing: [8, 4, 8, 4] },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { align: "left", verticalAlign: "top", itemStyle: { color: INK.secondary, fontWeight: "500" } },
      xAxis: {
        categories: summary.days.map((day) => axisDate.format(dateOf(day))),
        tickInterval: step,
        lineColor: INK.baseline,
        tickLength: 0,
        labels: { style: axisStyle },
        crosshair: { color: "rgba(91, 61, 153, 0.08)" },
      },
      yAxis: {
        title: { text: undefined },
        allowDecimals: false,
        min: 0,
        gridLineColor: INK.grid,
        labels: { style: axisStyle },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: "#ffffff",
        borderColor: INK.grid,
        borderRadius: 8,
        shadow: { color: "rgba(0,0,0,0.08)", width: 6, offsetX: 0, offsetY: 2 },
        formatter() {
          const index = this.index;
          const currentDay = summary.days[index];
          const previousDay = summary.previousDays[index];
          const row = (label: string, swatch: string, value: number | undefined) =>
            `<div style="display:flex;gap:8px;align-items:center;justify-content:space-between;min-width:180px">` +
            `<span style="display:flex;gap:6px;align-items:center;color:${INK.secondary}">` +
            `<span style="width:10px;height:3px;border-radius:2px;background:${swatch}"></span>${label}</span>` +
            `<b style="color:${INK.primary}">${value === undefined ? "–" : count.whole(value)}</b></div>`;
          return (
            `<div style="font-weight:600;margin-bottom:4px;color:${INK.primary}">${metricLabel[metric]}</div>` +
            row(currentDay ? longDate.format(dateOf(currentDay)) : "", color, current[index]) +
            row(previousDay ? longDate.format(dateOf(previousDay)) : "", PREVIOUS_PERIOD_COLOR, previous[index])
          );
        },
      },
      plotOptions: {
        series: { animation: false, marker: { enabled: false, radius: 4, symbol: "circle" }, lineWidth: 2 },
      },
      series: [
        {
          type: "line",
          name: t(($) => $.user_activity["Previous period"]),
          data: previous,
          color: PREVIOUS_PERIOD_COLOR,
          zIndex: 1,
        },
        {
          type: "area",
          name: t(($) => $.user_activity["This period"]),
          data: current,
          color,
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, `${color}33`],
              [1, `${color}00`],
            ],
          },
          zIndex: 2,
        },
      ],
    };
  }, [summary, metric, color, axisDate, longDate, count, t, metricLabel]);

  return (
    <Section
      title={t(($) => $.user_activity["Activity over time"])}
      description={t(($) => $.user_activity["Each day of this period, next to the same day of the period before."])}
      aside={
        <ButtonGroup<TrendMetric>
          compact
          value={metric}
          onSelect={setMetric}
          buttons={[
            { value: "activePeople", label: metricLabel.activePeople },
            { value: "changes", label: metricLabel.changes },
            { value: "views", label: metricLabel.views },
          ]}
        />
      }
    >
      <Chart options={options} />
    </Section>
  );
}

export function WorkSplitChart({ split }: { split: WorkSplit[] }) {
  const { t } = useTranslation(["user_activity"]);
  const labels = useActivityLabels();
  const count = useCount();

  const totals = split.reduce((sum, entry) => ({ team: sum.team + entry.team, trellis: sum.trellis + entry.trellis }), {
    team: 0,
    trellis: 0,
  });
  const all = totals.team + totals.trellis;

  const options = useMemo<Highcharts.Options>(
    () => ({
      chart: { type: "bar", height: 64 + split.length * 44, style: chartFont, spacing: [8, 24, 8, 4] },
      title: { text: undefined },
      credits: { enabled: false },
      legend: {
        align: "left",
        verticalAlign: "top",
        reversed: true,
        itemStyle: { color: INK.secondary, fontWeight: "500" },
      },
      xAxis: {
        categories: split.map((entry) => labels.kind(entry.kind)),
        lineWidth: 0,
        tickLength: 0,
        labels: { style: { color: INK.primary, fontSize: "13px" } },
      },
      yAxis: {
        title: { text: undefined },
        allowDecimals: false,
        gridLineColor: INK.grid,
        labels: { style: axisStyle },
        stackLabels: {
          enabled: true,
          style: { color: INK.secondary, fontWeight: "600", textOutline: "none" },
          formatter() {
            return count.whole(this.total);
          },
        },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: "#ffffff",
        borderColor: INK.grid,
        borderRadius: 8,
        formatter() {
          const rows = (this.points ?? [])
            .map(
              (point) =>
                `<div style="display:flex;gap:16px;justify-content:space-between">` +
                `<span style="color:${INK.secondary}">${point.series.name}</span>` +
                `<b style="color:${INK.primary}">${count.whole(point.y ?? 0)}</b></div>`,
            )
            .join("");
          return `<div style="font-weight:600;margin-bottom:4px;color:${INK.primary}">${String(this.x)}</div>${rows}`;
        },
      },
      plotOptions: {
        bar: {
          stacking: "normal",
          animation: false,
          pointWidth: 20,
          borderWidth: 2,
          borderColor: "#ffffff",
          borderRadius: 4,
        },
      },
      series: [
        {
          type: "bar",
          name: t(($) => $.user_activity["Trellis team"]),
          data: split.map((e) => e.trellis),
          color: TRELLIS_COLOR,
        },
        {
          type: "bar",
          name: t(($) => $.user_activity["Customer team"]),
          data: split.map((e) => e.team),
          color: TEAM_COLOR,
        },
      ],
    }),
    [split, labels, count, t],
  );

  return (
    <Section
      title={t(($) => $.user_activity["Who is doing the work"])}
      description={t(
        ($) => $.user_activity["Everything people did besides viewing pages, split by whose hands it was in."],
      )}
    >
      {all === 0 ?
        <EmptyNote>{t(($) => $.user_activity["Nobody did anything besides viewing pages in this period."])}</EmptyNote>
      : <>
          <Flex gap="large" flexWrap="wrap">
            <Headline color={TEAM_COLOR} value={count.percent(totals.team / all)}>
              {t(($) => $.user_activity["done by the customer's team"])}
            </Headline>
            <Headline color={TRELLIS_COLOR} value={count.percent(totals.trellis / all)}>
              {t(($) => $.user_activity["done by the Trellis team"])}
            </Headline>
          </Flex>
          <Chart options={options} />
        </>
      }
    </Section>
  );
}

function Headline({ color, value, children }: { color: string; value: string; children: string }) {
  return (
    <Flex alignItems="center" gap="small">
      <Box width="4px" alignSelf="stretch" borderRadius="pill" style={{ backgroundColor: color }} />
      <Box>
        <span className={css({ display: "block", color: "text.heading", fontSize: "26px", fontWeight: "700" })}>
          {value}
        </span>
        <span className={css({ color: "text.secondary", fontSize: "small" })}>{children}</span>
      </Box>
    </Flex>
  );
}

export function EmptyNote({ children }: { children: string }) {
  return (
    <p
      className={css({
        margin: 0,
        borderRadius: "light",
        paddingY: "large",
        paddingX: "medium",
        color: "text.secondary",
        textAlign: "center",
        backgroundColor: "#faf9fc",
      })}
    >
      {children}
    </p>
  );
}
